import { Avatar, Badge, Box, Flex, Group, Stack, Text } from "@mantine/core"
import { ImageIcon, PackageIcon } from "@phosphor-icons/react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { IPullSheetLine } from "../../models/PullSheetLine"
import { formatQuantity } from "../../utils/format-quantity"
import { ColumnType, Table } from "../table"
import RenderText from "../ui/render-text"

interface DeliveryNoteRef {
  id: string
  refCode?: string | null
  orderId?: string | null
}

interface Props {
  list: IPullSheetLine[]
  deliveryNotes?: DeliveryNoteRef[]
}

interface OrderGroup {
  orderId: string
  deliveryNote: DeliveryNoteRef | null
  lines: IPullSheetLine[]
  totalQuantity: number
}

export function SupplierShippingCustomerDetailDeliveryNoteList({ list, deliveryNotes }: Props) {
  const { t } = useTranslation(["delivery", "common"])
  const columns = useHeader()

  // Lines-ийг orderId-аар группдаж захиалга/DN тус бүрээр тусад нь
  // харуулна. orderId-аар нь delivery note-ийг ref code-той хослуулна.
  const groups = useMemo<OrderGroup[]>(() => {
    const dnByOrder = new Map<string, DeliveryNoteRef>()
    for (const dn of deliveryNotes ?? []) {
      if (dn.orderId) dnByOrder.set(dn.orderId, dn)
    }

    const byOrder = new Map<string, OrderGroup>()
    for (const line of list) {
      const orderId = line.orderId ?? "_unknown"
      let g = byOrder.get(orderId)
      if (!g) {
        g = {
          orderId,
          deliveryNote: dnByOrder.get(orderId) ?? null,
          lines: [],
          totalQuantity: 0,
        }
        byOrder.set(orderId, g)
      }
      g.lines.push(line)
      g.totalQuantity += Number(line.quantity ?? 0)
    }
    return Array.from(byOrder.values())
  }, [list, deliveryNotes])

  if (groups.length === 0) {
    return (
      <Box p="lg" ta="center">
        <Text size="sm" c="dimmed">
          {t("common:table.noData", { ns: "common", defaultValue: "Мэдээлэл байхгүй" })}
        </Text>
      </Box>
    )
  }

  // Ганц захиалгатай pull sheet-ийн хувьд header дэлгүүлэхгүй —
  // өмнөх UI-тэйгээ ижил харагдана.
  if (groups.length === 1) {
    return (
      <Table
        columns={columns}
        name="supplier-shipping-customer-detail-delivery-note.list"
        dataSource={groups[0].lines}
        pagination={false}
      />
    )
  }

  return (
    <Stack gap="lg">
      {groups.map((group, idx) => (
        <Box key={group.orderId}>
          <Flex
            align="center"
            justify="space-between"
            wrap="wrap"
            gap="sm"
            mb="xs"
            p="sm"
            style={{
              background: "var(--mantine-color-gray-0)",
              border: "1px solid var(--mantine-color-gray-2)",
              borderRadius: "var(--mantine-radius-md)",
            }}
          >
            <Group gap="sm" wrap="nowrap">
              <Box
                w={28}
                h={28}
                c="gray.8"
                bg="gray.1"
                style={{
                  borderRadius: "var(--mantine-radius-sm)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PackageIcon size={16} weight="duotone" />
              </Box>
              <Box>
                <Text size="sm" c="gray.8" fw={600}>
                  {t("delivery:table.deliveryNote", { defaultValue: "Хүргэлтийн баримт" })} {idx + 1}/{groups.length}
                </Text>
                <Group gap="xs" mt={2}>
                  <Text size="sm" fw={700} c="dark" ff="monospace">
                    {group.deliveryNote?.refCode ?? "-"}
                  </Text>
                </Group>
              </Box>
            </Group>
            <Group gap="md">
              <Badge size="md" variant="light" color="gray" radius="sm">
                {t("delivery:table.lineCount", {
                  defaultValue: "{{count}} мөр",
                  count: group.lines.length,
                })}
              </Badge>
              <Badge size="md" variant="light" color="gray" radius="sm">
                {t("delivery:table.totalQuantity", { defaultValue: "Нийт" })}: {formatQuantity(group.totalQuantity)}
              </Badge>
            </Group>
          </Flex>
          <Table
            columns={columns}
            name={`supplier-shipping-customer-detail-delivery-note.list.${group.orderId}`}
            dataSource={group.lines}
            pagination={false}
          />
        </Box>
      ))}
    </Stack>
  )
}

const useHeader = (): ColumnType<IPullSheetLine>[] => {
  const { t } = useTranslation(["delivery", "common"])
  return [
    {
      title: "#",
      render: (_r, index) => <RenderText text={index + 1} />,
      width: "80px",
    },
    {
      title: t("delivery:table.image"),
      dataIndex: "image",
      render: (record) => (
        <Avatar src={record?.image || null} radius="xs">
          <ImageIcon />
        </Avatar>
      ),
    },
    {
      title: t("delivery:table.dehubCode"),
      dataIndex: "refCode",
      render: (record) => {
        return <RenderText text={record?.refCode} color="var(--mantine-color-dark-5)" />
      },
    },
    {
      title: t("delivery:table.sku"),
      dataIndex: "skuCode",
      render: (record) => {
        return <RenderText text={record?.skuCode} color="var(--mantine-color-dark-5)" />
      },
    },
    {
      title: t("delivery:table.productName"),
      dataIndex: "name",
      render: (record) => {
        return (
          <RenderText
            text={record?.optionValue ? `${record?.name ?? "-"}, ${record.optionValue}` : (record?.name ?? "-")}
            color="var(--mantine-color-dark-5)"
          />
        )
      },
    },
    {
      title: t("delivery:table.unit"),
      dataIndex: "unit",
      render: (record) => {
        return <RenderText text={record?.unit || record?.unit} color="var(--mantine-color-dark-5)" />
      },
    },
    {
      title: t("delivery:table.approvedQty"),
      dataIndex: "unit.quantity",
      render: (record) => {
        return <RenderText text={formatQuantity(record?.quantity)} color="var(--mantine-color-dark-5)" />
      },
    },
    {
      title: t("delivery:table.loadedQty"),
      dataIndex: "unit.confirmedQuantity",
      render: (record) => {
        return <RenderText text={formatQuantity(record?.confirmedQuantity)} color="var(--mantine-color-dark-5)" fontWeight={700} />
      },
    },
  ]
}
